import { BorderRadius, Color, DefaultValue, getDefaultTransition, Spacing } from '@billy/design-system'
import { css } from '@emotion/core'
import styled from '@emotion/styled'

import { FORMS_INNER_SHADOW } from '../../styles-deprecated'

const DEFAULT_GAP_SIZE = 10

export const FormComponent = styled.form`
  margin: 0;

  > * {
    transition:
      background-color 0.1s linear,
      border-color 0.1s linear;
  }
`

export const FormRow = styled.div`
  display: grid;
  gap: ${DEFAULT_GAP_SIZE}px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: min-content;
`

export const Error = styled.div`
  grid-column: 1 / -1;
  margin-top: -${DEFAULT_GAP_SIZE * 2}px;
`

interface InputProps {
  error?: boolean
}

export const Input = styled.input<InputProps>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 1px solid ${({ error }) => (error ? Color.Red700 : Color.Grey300)};
  border-color: ${Color.Grey200};
  border-radius: ${BorderRadius.R6};
  position: relative;
  background-color: ${Color.White};
  box-shadow: ${FORMS_INNER_SHADOW};
  cursor: pointer;
  appearance: none;
  ${getDefaultTransition('background-color', 'border', 'box-shadow')};

  ${({ error }) =>
    !error &&
    css`
      &:hover {
        border: 1px solid ${Color.Grey300};
        background-color: ${Color.Grey50};
      }
    `}
  &:active {
    border-color: ${Color.Grey300};
    background-color: ${Color.Grey50};
    box-shadow: none;
  }
  &:checked {
    background-color: ${({ error }) => (error ? Color.Red700 : Color.Lavender500)};
    box-shadow: none;
    color: ${Color.White};

    &:before {
      content: '';
      width: 10px;
      height: 10px;
      position: absolute;
      top: 1px;
      left: 2px;
      background-image: url("data:image/svg+xml,%3Csvg width='10' height='11' viewBox='0 0 10 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5.87012L3.66667 8.37012L9 2.37012' stroke='white' stroke-width='1.5'/%3E%3C/svg%3E%0A");
    }
    &:hover {
      background-color: ${({ error }) => (error ? Color.Red700 : Color.Lavender600)};
      color: ${Color.White};

      &:before {
        background-image: url("data:image/svg+xml,%3Csvg width='10' height='11' viewBox='0 0 10 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5.87012L3.66667 8.37012L9 2.37012' stroke='white' stroke-width='1.5'/%3E%3C/svg%3E%0A");
      }
    }
    &:active {
      background-color: ${({ error }) => (error ? Color.Red700 : Color.Lavender600)};
      box-shadow: none;
      color: ${Color.White};
    }
  }
  &:disabled {
    border-color: ${Color.Grey200};
    background-color: ${Color.Grey100};
    cursor: not-allowed;

    &:before {
      background-image: url("data:image/svg+xml,%3Csvg width='10' height='11' viewBox='0 0 10 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5.87012L3.66667 8.37012L9 2.37012' stroke='silver' stroke-width='1.5'/%3E%3C/svg%3E%0A");
    }

    &,
    &:hover {
      border-color: ${Color.Grey200};
      background-color: ${Color.Grey100};
    }
    &:checked {
      border-color: ${Color.Grey200};
      background-color: ${Color.Grey100};
      color: ${Color.Grey200};
    }
  }
`

export const DateCheckboxInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  flex-grow: 1;

  .react-datepicker-wrapper div {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`

interface DateCheckboxWrapperProps {
  disabled?: boolean
}

export const DateCheckboxWrapper = styled.div<DateCheckboxWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: ${Spacing.S8} ${Spacing.S12};
  border: 1px solid ${Color.Grey200};
  border-left-color: transparent;
  border-top-right-radius: ${DefaultValue.BorderRadius};
  border-bottom-right-radius: ${DefaultValue.BorderRadius};

  label span {
    display: none;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      border-color: ${Color.Grey200};
      background-color: ${Color.Grey100};
      box-shadow: none;
      color: ${Color.Grey200};

      &::placeholder: {
        color: ${Color.Grey200};
      }
    `}
`

export const ErrorContainer = styled.div`
  &:not:empty: {
    margin-bottom: ${Spacing.S12};
  }
`

interface CheckboxLabelProps {
  disabled?: boolean
}

export const CheckboxLabel = styled.label<CheckboxLabelProps>`
  display: flex;
  justify-content: flex-start;
  ${({ disabled }) => (!disabled ? 'cursor: pointer' : '')};
`

export const FormLabel = styled.label`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.S4};
  color: ${Color.Grey500};
`
